import { aircraftType } from '../data/aircraft';
import { QUIZ_QUESTIONS, type QuizQuestion } from '../data/quizQuestions';
import type { AircraftAsset, GameState, ActiveEffect, ResearchOpportunity } from './types';

const clone = <T,>(value: T): T => structuredClone(value);

export function quizThreshold(week: number): number {
  if (week < 52) return 16;
  if (week < 156) return 12;
  return 10;
}

export function selectResearchQuestion(seed: number, week: number): QuizQuestion {
  const index = Math.abs(Math.trunc(seed * 31 + week * 17)) % QUIZ_QUESTIONS.length;
  return QUIZ_QUESTIONS[index];
}

export function createResearchOpportunity(state: GameState): ResearchOpportunity {
  return {
    questionId: selectResearchQuestion(state.seed, state.week).id,
    offeredWeek: state.week,
    expiresWeek: state.week + quizThreshold(state.week),
    status: 'available',
  };
}

export function sellOneAircraft(next: GameState, aircraft: AircraftAsset): number {
  const type = aircraftType(aircraft.typeId);
  const salePrice = Math.round(aircraft.bookValue * (0.72 + aircraft.condition * 0.18));
  const loan = aircraft.loanId ? next.loans.find((l) => l.id === aircraft.loanId) : undefined;
  const payoff = loan?.principalRemaining ?? 0;
  const proceeds = salePrice - payoff;
  next.cash += proceeds;
  for (const routeId of aircraft.routeIds) {
    const route = next.routes.find((r) => r.id === routeId);
    if (route) route.aircraftIds = route.aircraftIds.filter((a) => a !== aircraft.id);
  }
  next.aircraft = next.aircraft.filter((a) => a.id !== aircraft.id);
  if (loan) next.loans = next.loans.filter((l) => l.id !== loan.id);
  next.ledger.push({
    id: `ledger-${next.nextId++}`, week: next.week,
    category: 'asset_sale', cashAmount: proceeds, profitAmount: salePrice - aircraft.bookValue,
    objectId: aircraft.id, label: `现金自救出售 ${type.name}`,
  });
  return proceeds;
}

export function getPenaltyCount(week: number): number {
  if (week < 52) return 0;
  if (week < 156) return 1;
  return 2;
}

function applyLateResearchPenalty(next: GameState): void {
  const lateStage = getPenaltyCount(next.week);
  if (lateStage === 0) return;

  const listed = next.companyListing.listed;
  const storageCount = next.fuelStorage.filter((facility) => facility.remainingLifeWeeks > 0).length;
  const hasLargeFuelOperation = storageCount > 10;
  const baseOperatingCosts = Math.max(next.lastReport?.operatingCosts ?? 0, next.cash * 0.004, 250_000);
  const cashPenaltyRate = lateStage === 2 ? 0.12 : 0.07;
  const complexitySurcharge = (listed ? 0.04 : 0) + (hasLargeFuelOperation ? 0.04 : 0);
  const cashPenalty = Math.min(
    next.cash,
    Math.round(Math.max(100_000, baseOperatingCosts * (cashPenaltyRate + complexitySurcharge))),
  );
  next.cash -= cashPenalty;

  const revenuePenalty = Math.max(
    0.78,
    1 - (lateStage === 2 ? 0.08 : 0.04) - (listed ? 0.025 : 0) - (hasLargeFuelOperation ? 0.015 : 0),
  );
  const revenueEffectId = `effect-${next.nextId++}`;
  next.activeEffects = [
    ...next.activeEffects,
    {
      id: revenueEffectId,
      kind: 'revenueModifier',
      multiplier: revenuePenalty,
      weeksRemaining: lateStage === 2 ? 6 : 4,
    },
  ];

  const reputationPenalty = (lateStage === 2 ? 3 : 2) + (listed ? 1 : 0) + (hasLargeFuelOperation ? 1 : 0);
  next.reputation = Math.max(30, next.reputation - reputationPenalty);

  const extraEffects: string[] = [`现金 -$${cashPenalty.toLocaleString('zh-CN')}`, `未来营收 -${Math.round((1 - revenuePenalty) * 100)}%`];
  if (listed) {
    const sharePricePenalty = lateStage === 2 ? 0.88 : 0.93;
    const nextSharePrice = Math.max(0.1, next.companyListing.sharePrice * sharePricePenalty);
    next.companyListing = {
      ...next.companyListing,
      sharePrice: nextSharePrice,
      sharePriceHistory: [...(next.companyListing.sharePriceHistory ?? []), { week: next.week, price: nextSharePrice }].slice(-104),
    };
    extraEffects.push(`股价 -${Math.round((1 - sharePricePenalty) * 100)}%`);
  }
  if (hasLargeFuelOperation) {
    const fuelPenalty = lateStage === 2 ? 1.15 : 1.08;
    next.activeEffects = [
      ...next.activeEffects,
      { id: `effect-${next.nextId++}`, kind: 'fuelCostModifier', multiplier: fuelPenalty, weeksRemaining: lateStage === 2 ? 6 : 4 },
    ];
    extraEffects.push(`燃油成本 +${Math.round((fuelPenalty - 1) * 100)}%`);
  }

  next.events = [
    ...(next.events ?? []),
    {
      id: `event-${next.nextId++}`,
      week: next.week,
      tone: 'negative' as const,
      title: '城市调研判断失误',
      detail: `第 ${next.week} 周答题错误：声誉 -${reputationPenalty}，${extraEffects.join('，')}。${lateStage === 2 ? '长期经营阶段的失误会持续更久。' : ''}`,
      category: 'risk' as const,
      targetLabel: '城市调研',
    },
  ].slice(-36);
  next.ledger.push({
    id: `ledger-${next.nextId++}`,
    week: next.week,
    category: 'operating_expense',
    cashAmount: -cashPenalty,
    profitAmount: -cashPenalty,
    objectId: 'research-penalty',
    label: `城市调研判断失误：现金 -$${cashPenalty.toLocaleString('zh-CN')}`,
  });
}

export function applyQuizResult(state: GameState, correct: boolean): GameState {
  const next = clone(state);

  if (correct) {
    const effectId = `effect-${next.nextId++}`;
    next.activeEffects = [
      ...next.activeEffects,
      { id: effectId, kind: 'revenueModifier' as const, multiplier: 1.03, weeksRemaining: 4 },
    ];
    next.reputation = Math.min(100, next.reputation + 1);
    next.ledger.push({
      id: `ledger-${next.nextId++}`, week: next.week,
      category: 'operating_expense', cashAmount: 0, profitAmount: 0,
      label: '城市调研完成：获得 4 周轻量营收洞察',
    });
  } else {
    applyLateResearchPenalty(next);
    next.ledger.push({
      id: `ledger-${next.nextId++}`, week: next.week,
      category: 'operating_expense', cashAmount: 0, profitAmount: 0,
      label: '城市调研未形成可用洞察',
    });
  }

  return next;
}

export function applyResearchResult(state: GameState, correct: boolean): GameState {
  const next = applyQuizResult(state, correct);
  const opportunity = next.researchOpportunity;
  if (!opportunity) return next;
  next.researchHistory = [
    ...(next.researchHistory ?? []),
    { questionId: opportunity.questionId, resolvedWeek: next.week, correct },
  ].slice(-60);
  next.researchOpportunity = undefined;
  next.lastQuizWeek = next.week;
  next.quizCounter = 0;
  next.pendingQuiz = false;
  return next;
}

export function decrementEffects(state: GameState): GameState {
  return {
    ...state,
    activeEffects: state.activeEffects
      .map((e: ActiveEffect) => ({ ...e, weeksRemaining: e.weeksRemaining - 1 }))
      .filter((e: ActiveEffect) => e.weeksRemaining > 0),
  };
}
